// Dynamic Download Manager - Fetches and parses Apache directory listing
// Works with CORS disabled browser or in production (same origin)

const DOWNLOAD_BASE_URL = "https://teamgrid.ai/downloads";

// Parse Apache directory listing HTML
const parseDirectoryListing = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const files = [];

  // Find all links in the table
  const links = doc.querySelectorAll("a[href]");

  links.forEach((link) => {
    let href = link.getAttribute("href");
    const text = link.textContent.trim();

    // Skip parent directory, directories, and non-file links
    if (href === "/downloads/" || href === "/" || href.startsWith("?") || !text || href.endsWith("/")) return;
    
    // Skip directory entries (they have folder icons or are in <DIR> rows)
    const row = link.closest("tr");
    if (!row) return;
    
    // Check if this is a directory (look for folder icon or DIR text in row)
    const rowText = row.textContent || "";
    if (rowText.includes("[DIR]") || rowText.includes("Parent Directory")) return;

    // Get the table row to extract size and date
    const cells = row.querySelectorAll("td");
    if (cells.length >= 4) {
      const dateText = cells[2]?.textContent?.trim() || "";
      const sizeText = cells[3]?.textContent?.trim() || "";

      // Use text content as filename (it's already decoded), but href might be URL-encoded
      const filename = text;
      // Use href for URL (it's already URL-encoded if needed)
      // If href doesn't start with /, it's relative, so we need to handle it
      if (!href.startsWith("http") && !href.startsWith("/")) {
        href = href; // Keep as is, it's relative
      }

      const version = extractVersion(filename);
      const platform = detectPlatform(filename);

      if (platform && version) {
        // Construct full URL - href might already be URL-encoded
        const urlPath = href.startsWith("/") ? href : `/${href}`;
        files.push({
          filename,
          href: urlPath,
          version,
          platform,
          lastModified: dateText,
          size: sizeText,
          fullUrl: `${DOWNLOAD_BASE_URL}${urlPath}`,
        });
      }
    }
  });

  console.log("Parsed files:", files);
  return files;
};

// Extract version from filename
const extractVersion = (filename) => {
  const patterns = [
    /(\d+\.\d+\.\d+)/, // 1.0.12
    /v(\d+\.\d+\.\d+)/, // v1.0.12
    /_(\d+\.\d+\.\d+)/, // _1.0.12
  ];

  for (const pattern of patterns) {
    const match = filename.match(pattern);
    if (match) {
      return match[1];
    }
  }
  return null;
};

// Detect platform from filename
const detectPlatform = (filename) => {
  const lowerName = filename.toLowerCase();

  if (lowerName.includes("setup") && lowerName.endsWith(".exe")) {
    return "windows";
  } else if (lowerName.includes("arm64") && lowerName.endsWith(".dmg")) {
    return "mac-arm64";
  } else if (lowerName.endsWith(".dmg")) {
    return "mac-intel";
  } else if (lowerName.endsWith(".deb")) {
    return "ubuntu";
  } else if (lowerName.endsWith(".appimage")) {
    return "ubuntu-arm64";
  }

  return null;
};

// Compare versions
const compareVersions = (v1, v2) => {
  const parts1 = v1.split(".").map(Number);
  const parts2 = v2.split(".").map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;

    if (part1 > part2) return 1;
    if (part1 < part2) return -1;
  }

  return 0;
};

// Get latest file for each platform
const getLatestFiles = (files) => {
  const latestByPlatform = {};

  files.forEach((file) => {
    const current = latestByPlatform[file.platform];

    if (!current || compareVersions(file.version, current.version) > 0) {
      latestByPlatform[file.platform] = file;
    }
  });

  return latestByPlatform;
};

// Main function to fetch and parse directory listing
export const fetchLatestDownloads = async () => {
  try {
    console.log("Fetching directory listing from:", `${DOWNLOAD_BASE_URL}/`);

    const response = await fetch(`${DOWNLOAD_BASE_URL}/`, {
      method: "GET",
      headers: {
        Accept:
          "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const html = await response.text();
    console.log("Fetched HTML length:", html.length);

    const files = parseDirectoryListing(html);

    const latestFiles = getLatestFiles(files);

    console.log("Latest files by platform:", latestFiles);

    // Transform to expected format
    const result = {};
    Object.keys(latestFiles).forEach((platform) => {
      const file = latestFiles[platform];
      result[platform] = {
        url: file.fullUrl,
        version: file.version,
        filename: file.filename,
        size: file.size,
      };
    });

    // Add mac alias
    if (result["mac-intel"]) {
      result["mac"] = result["mac-intel"];
    }

    console.log("Final download URLs:", result);
    return result;
  } catch (error) {
    console.error("Error fetching downloads:", error);
    throw error; // Let the component handle the error
  }
};

export default {
  fetchLatestDownloads,
  DOWNLOAD_BASE_URL,
};
